import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Check, X, RotateCcw, Search, SlidersHorizontal, Plus, RefreshCw, Mail, FileText, ChevronsLeftRight } from 'lucide-react'
import PdfViewer from '../components/PdfViewer'
import OrderReview from '../components/OrderReview'
import NewOrderModal from '../components/NewOrderModal'
import ConfirmActionModal from '../components/ConfirmActionModal'
import useIsMobile from '../hooks/useIsMobile'
import useAttachmentPreview from '../hooks/useAttachmentPreview'
import { MOCK_ORDERS, STATUS_META, STATUS_SORT_RANK, FILTER_TABS, formatDateTime, orderOrigin } from '../data/mockOrders'

// Percorso relativo (stessa origine): vedi commento in useAttachmentPreview.js
const API_BASE = ''
const POLL_INTERVAL_MS = 5000

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result.split(',')[1] || '')
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export default function Dashboard() {
  const isMobile = useIsMobile()
  const [state, setState] = useState('list') // list | review | approved | rejected
  const [orders, setOrders] = useState([])
  const [selectedOrderIdx, setSelectedOrderIdx] = useState(null)
  const [currentJob, setCurrentJob] = useState(null)
  const [formData, setFormData] = useState({})
  const [actionLoading, setActionLoading] = useState(false)
  // Popup di conferma prima di Concludi/Rifiuta (richiesta esplicita
  // 2026-08-12) — null | 'approve' | 'reject': quale azione è in attesa di
  // conferma nel popup. I bottoni non chiamano più handleApprove/handleReject
  // direttamente, solo dopo la conferma nel popup (vedi ConfirmActionModal).
  const [pendingAction, setPendingAction] = useState(null)
  const {
    pdfDocuments,
    originalFile,
    loading: attachmentLoading,
  } = useAttachmentPreview(currentJob)
  // Contenitore scrollabile condiviso da tutti i PDF impilati in colonna
  // (vedi PdfViewer.jsx `scrollContainerRef`) — un solo scroll per l'intero
  // documento multi-allegato, non uno per PDF.
  const multiPdfScrollerRef = useRef(null)

  // Ricerca e filtri lista ordini
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showDateFilter, setShowDateFilter] = useState(false)
  const [showNewOrderModal, setShowNewOrderModal] = useState(false)
  const [refreshTick, setRefreshTick] = useState(0)
  const handleRefresh = () => setRefreshTick((t) => t + 1)

  // Divisore ridimensionabile tra documento e campi estratti (vista review)
  // — proporzione di default 40/60 (PDF/righe) su richiesta, non più 55/45:
  // resta comunque ridimensionabile a mano dall'utente.
  const reviewBodyRef = useRef(null)
  const [leftWidthPct, setLeftWidthPct] = useState(40)
  const [isDragging, setIsDragging] = useState(false)

  // Collegamento documento↔riga ordine (vedi OrderReview.jsx/PdfViewer.jsx):
  // - hoveredLineText: mentre il puntatore è su una riga, il documento a
  //   sinistra la cerchia (bordo verde) — niente scroll, solo riferimento
  //   visivo, sparisce quando il puntatore esce dalla riga.
  // - scrollRequest: doppio click su una riga → porta quel punto in cima al
  //   documento. `nonce` incrementa a ogni doppio click (anche sulla stessa
  //   riga più volte) così l'effetto in PdfViewer si riattiva sempre, non
  //   solo quando cambia il testo.
  // Entrambi azzerati a ogni cambio ordine per non mostrare per un istante
  // riferimenti all'ordine precedente.
  const [hoveredLineText, setHoveredLineText] = useState(null)
  const [scrollRequest, setScrollRequest] = useState({ text: null, nonce: 0 })
  // Testi delle righe ordine confermate — un riquadro verde fisso per
  // ciascuna nel documento a sinistra, vedi OrderReview.jsx
  // `onConfirmedLinesChange` / PdfViewer.jsx `confirmedTexts`.
  const [confirmedLineTexts, setConfirmedLineTexts] = useState([])

  // Banner rosso errori di sistema (sopra "Recap. ordine" in OrderReview.jsx):
  // aggrega errori da fonti diverse in un'unica lista {code, message} —
  // pdfError (caricamento PDF fallito, da PdfViewer via onError) e
  // actionError (chiamata Approva/Rifiuta fallita, da submitDecision).
  // A questi si aggiunge, dentro OrderReview stesso, l'eventuale
  // `job.system_error` già presente nel job (es. matching andato in errore
  // lato Queuer) — quello non serve sollevarlo qui perché non nasce in un
  // componente fratello, arriva già dentro `job`.
  const [pdfError, setPdfError] = useState(null)
  // Ultimi override scelti a mano dentro OrderReview (cliente/destinazione/
  // articoli cercati manualmente) — un ref, non uno state: cambia ad ogni
  // tocco dell'utente e non deve causare un re-render di Dashboard, serve
  // solo al momento della POST in submitDecision.
  const overridesRef = useRef({ client_code: null, destination_code: null, lines: [] })
  const [actionError, setActionError] = useState(null)
  const systemErrors = [actionError, pdfError].filter(Boolean)

  useEffect(() => {
    setHoveredLineText(null)
    setScrollRequest({ text: null, nonce: 0 })
    setConfirmedLineTexts([])
    setActionError(null)
    setPendingAction(null)
    // pdfError si azzera da solo dentro PdfViewer quando cambia l'allegato
    // (stesso effetto che tenta il caricamento), non serve azzerarlo qui.
  }, [currentJob?.job_id])

  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e) => {
      if (!reviewBodyRef.current) return
      const rect = reviewBodyRef.current.getBoundingClientRect()
      let pct = ((e.clientX - rect.left) / rect.width) * 100
      pct = Math.min(75, Math.max(25, pct))
      setLeftWidthPct(pct)
    }
    const handleMouseUp = () => setIsDragging(false)

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  // Polling reale su backend-consumer (GET /api/jobs)
  useEffect(() => {
    let cancelled = false

    const fetchJobs = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs`)
        if (!res.ok) throw new Error(`status ${res.status}`)
        const data = await res.json()
        if (!cancelled) setOrders(data.jobs || [])
      } catch (err) {
        console.warn('[DEV MOCK] backend-consumer non raggiungibile, uso dati finti:', err.message)
        if (!cancelled) setOrders(MOCK_ORDERS)
      }
    }

    fetchJobs()
    const interval = setInterval(fetchJobs, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [refreshTick])

  // N° Ordine Jenia: numerazione progressiva (J-1, J-2...) assegnata in
  // ordine cronologico di arrivo. TODO: oggi è calcolata lato client sugli
  // ordini attualmente caricati in `orders`, quindi cambia se un ordine
  // esce dalla lista (es. approvato/rifiutato, vedi handleNextOrder) — non è
  // stabile nel tempo. Quando esisterà un contatore reale lato backend
  // (Core/database-ms), sostituire con il valore che arriva nel job.
  const jeniaOrderNumbers = useMemo(() => {
    const chronological = [...orders].sort((a, b) => {
      const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return tsA - tsB
    })
    const map = new Map()
    chronological.forEach((order, idx) => map.set(order.job_id, `J-${idx + 1}`))
    return map
  }, [orders])

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : null
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null

    return orders.filter((order) => {
      if (statusFilter !== 'all' && order.status !== statusFilter) return false

      if (from || to) {
        const ts = order.timestamp ? new Date(order.timestamp) : null
        if (!ts) return false
        if (from && ts < from) return false
        if (to && ts > to) return false
      }

      if (q) {
        const haystack = [
          order.order_number,
          order.client_name,
          order.sender,
          order.subject,
          order.assigned_user?.name,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(q)) return false
      }

      return true
    }).sort((a, b) => {
      // Prima gli ordini da revisionare (serve intervenire), poi approvati e
      // rifiutati in fondo; a parità di stato, il più recente prima.
      const rankA = STATUS_SORT_RANK[a.status] ?? 2
      const rankB = STATUS_SORT_RANK[b.status] ?? 2
      if (rankA !== rankB) return rankA - rankB
      const tsA = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const tsB = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return tsB - tsA
    })
  }, [orders, searchQuery, statusFilter, dateFrom, dateTo])

  const initializeFormData = (job) => {
    if (job.matched_record) {
      setFormData({
        job_id: job.job_id,
        sku: job.matched_record.matched_id || '',
        product: job.matched_record.matched_product || '',
        quantity: job.matched_record.matched_quantity || '',
        price: '',
        delivery_date: '',
        notes: '',
      })
    } else {
      setFormData({
        job_id: job.job_id,
        sku: '',
        product: '',
        quantity: '',
        price: '',
        delivery_date: '',
        notes: '',
      })
    }
  }

  // Crea un ordine inserito manualmente (PDF caricato o testo mail incollato)
  // per i casi non rilevati automaticamente dalla pipeline. TODO: oggi
  // l'ordine resta solo lato client — quando esisterà un endpoint reale nel
  // Core per l'inserimento manuale, sostituire con una POST che avvia il
  // job vero (parser/LLM/matcher) invece di crearlo già "completed".
  const handleCreateManualOrder = async ({ mode, file, text }) => {
    const job_id = `manual-${Date.now()}`
    let manualPdfBase64 = null

    if (mode === 'pdf' && file) {
      try {
        manualPdfBase64 = await blobToBase64(file)
      } catch (err) {
        console.error('Errore nella lettura del PDF caricato:', err.message)
      }
    }

    const newOrder = {
      job_id,
      status: 'completed',
      sender: 'Caricamento manuale',
      subject: mode === 'pdf' ? (file?.name || 'Nuovo ordine (PDF)') : 'Nuovo ordine (testo incollato)',
      client_name: null,
      client_piva: null,
      client_code: null,
      client_note: '',
      order_number: null,
      assigned_user: null,
      line_count: 0,
      confidence: null,
      elapsed_seconds: null,
      manual_pdf_base64: manualPdfBase64,
      body: mode === 'text' ? text : '',
      lines: [],
      matched_record: null,
      match_result: null,
      attachments: [],
      timestamp: new Date().toISOString(),
    }

    setOrders((prev) => [newOrder, ...prev])
    setShowNewOrderModal(false)
  }

  // Il codice errore (`err.code`) è quello che arriverà dal backend quando
  // esisterà un campo dedicato (es. `body.error_code`); finché non c'è,
  // ricade sullo status HTTP — comunque un codice, utile per il badge nel
  // banner rosso (vedi systemErrors/OrderReview.jsx).
  const submitDecision = async (decision) => {
    // overrides solo su approve: un rifiuto non scrive nulla, non ha senso
    // farlo fallire per una destinazione/cliente non trovato in anagrafica.
    const overrides = decision === 'approve' ? overridesRef.current : undefined
    const res = await fetch(`${API_BASE}/api/jobs/${currentJob.job_id}/decision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, overrides }),
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      const problemi = body?.detail?.problemi
      const err = new Error(problemi ? problemi.join('; ') : (body?.error || `status ${res.status}`))
      err.code = body?.error_code || res.status
      throw err
    }
    return body
  }

  const handleApprove = async () => {
    if (!currentJob) return
    setActionLoading(true)
    setActionError(null)
    try {
      await submitDecision('approve')
      setState('approved')
      handleNextOrder()
    } catch (err) {
      setActionError({ code: err.code ?? null, message: `Impossibile approvare: ${err.message}` })
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!currentJob) return
    setActionLoading(true)
    setActionError(null)
    try {
      await submitDecision('reject')
      setState('rejected')
      handleNextOrder()
    } catch (err) {
      setActionError({ code: err.code ?? null, message: `Impossibile rifiutare: ${err.message}` })
    } finally {
      setActionLoading(false)
    }
  }

  // "Modifica" su un ordine rifiutato: lo riporta in revisione (Conferma/
  // Rifiuta tornano visibili, campi di nuovo editabili) — SOLO per i
  // rifiutati, il backend rifiuta la richiesta per qualunque altro stato
  // (vedi backend-consumer.js:/api/jobs/:jobId/reopen). Resta sulla stessa
  // schermata di review, non naviga via come handleApprove/handleReject.
  const handleReopen = async () => {
    if (!currentJob) return
    setActionLoading(true)
    setActionError(null)
    try {
      const res = await fetch(`${API_BASE}/api/jobs/${currentJob.job_id}/reopen`, { method: 'POST' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body?.error || `status ${res.status}`)
      setCurrentJob((prev) => (prev ? { ...prev, status: 'completed' } : prev))
    } catch (err) {
      setActionError({ code: err.code ?? null, message: `Impossibile riaprire l'ordine: ${err.message}` })
    } finally {
      setActionLoading(false)
    }
  }

  const handleNextOrder = () => {
    setOrders(orders.filter((_, i) => i !== selectedOrderIdx))
    setState('list')
    setCurrentJob(null)
    setFormData({})
    setSelectedOrderIdx(null)
  }

  const orderLabel = (order) =>
    order.subject || order.sender || order.matched_record?.description || order.job_id

  const handleReset = () => {
    setState('list')
    setCurrentJob(null)
    setFormData({})
    setSelectedOrderIdx(null)
  }

  const selectOrder = (order) => {
    const idx = orders.findIndex((o) => o.job_id === order.job_id)
    setSelectedOrderIdx(idx)
    setCurrentJob(order)
    initializeFormData(order)
    setState('review')
  }

  const backToList = () => {
    setState('list')
    setCurrentJob(null)
    setFormData({})
    setSelectedOrderIdx(null)
  }

  const clearDateFilter = () => {
    setDateFrom('')
    setDateTo('')
    setShowDateFilter(false)
  }

  const dateFilterActive = Boolean(dateFrom || dateTo)

  return (
    <div style={s.layout}>

      {state === 'list' && (
        <div style={s.listContainer} className="animate-fade-in">
          <div style={s.listHeader}>
            <div>
              <div style={s.listTitleRow}>
                <h2 style={s.listTitle}>Storico</h2>
                <span style={s.sidebarCount}>{orders.length}</span>
              </div>
              <p style={s.listSubtitle}>
                Tutto quello che ho processato. {filteredOrders.length} di {orders.length} ordini.
              </p>
            </div>
            <div style={s.headerActions}>
              <button style={s.refreshBtn} onClick={handleRefresh}>
                <RefreshCw size={14} />
                Aggiorna
              </button>
              {/* "Nuovo ordine" nascosto temporaneamente: manca ancora il supporto
                  a backend per l'inserimento manuale. Il modale (NewOrderModal) e
                  tutta la logica restano intatti sotto — basta rimettere il bottone
                  per riattivarlo. */}
              {false && (
                <button style={s.newOrderBtn} onClick={() => setShowNewOrderModal(true)}>
                  <Plus size={15} />
                  Nuovo ordine
                </button>
              )}
            </div>
          </div>

          <div style={s.toolbar}>
            <div style={s.searchBox}>
              <Search size={15} color="var(--text-3)" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca per n. ordine, cliente, utente..."
                style={s.searchInput}
              />
            </div>

            <div style={s.filterTabs}>
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilter(tab.key)}
                  style={{
                    ...s.filterTab,
                    ...(statusFilter === tab.key ? s.filterTabActive : {}),
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowDateFilter((v) => !v)}
                style={{
                  ...s.filterBtn,
                  ...(dateFilterActive ? s.filterBtnActive : {}),
                }}
              >
                <SlidersHorizontal size={13} />
                Filtra
              </button>
              {showDateFilter && (
                <div style={s.dateFilterPopover}>
                  <div style={s.dateFilterRow}>
                    <label style={s.dateFilterLabel}>Da</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      style={s.dateFilterInput}
                    />
                  </div>
                  <div style={s.dateFilterRow}>
                    <label style={s.dateFilterLabel}>A</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      style={s.dateFilterInput}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button className="btn" style={{ flex: 1, height: 28 }} onClick={clearDateFilter}>Reset</button>
                    <button className="btn btn-primary" style={{ flex: 1, height: 28 }} onClick={() => setShowDateFilter(false)}>Applica</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isMobile ? (
            <div style={s.mobileList}>
              {filteredOrders.map((order) => {
                const meta = STATUS_META[order.status] || { label: order.status || '—', color: 'var(--text-3)', bg: 'var(--bg-subtle)', border: 'var(--hairline)' }
                return (
                  <button key={order.job_id} style={s.mobileCard} onClick={() => selectOrder(order)}>
                    <div style={s.mobileCardTop}>
                      <span style={{
                        ...s.statusBadge,
                        color: meta.color,
                        background: meta.bg,
                        border: `1px solid ${meta.border}`,
                      }}>
                        {meta.label}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{formatDateTime(order.timestamp)}</span>
                    </div>
                    <div style={s.orderItemName}>{order.client_name || order.sender || '—'}</div>
                    <div style={s.originRow}>
                      {orderOrigin(order) === 'mail' ? <Mail size={12} /> : <FileText size={12} />}
                      {orderOrigin(order) === 'mail' ? 'Mail' : 'Documento'}
                    </div>
                    <div style={s.listRowSub}>{order.subject || order.sender}</div>
                    <div style={s.mobileCardFooter}>
                      <span>{order.sender || 'Nessuno'}</span>
                      <span>{order.order_number || '—'}</span>
                      <span>{order.line_count ?? '—'} righe</span>
                      <span>{order.elapsed_seconds != null ? `${order.elapsed_seconds.toFixed(1).replace('.', ',')}s` : '—'}</span>
                    </div>
                  </button>
                )
              })}
              {filteredOrders.length === 0 && (
                <div style={s.listEmpty}>Nessun ordine corrisponde ai filtri.</div>
              )}
            </div>
          ) : (
            <div style={s.listTable}>
              <div style={s.listRowHeader}>
                <div>Stato</div>
                <div>Data / Ora</div>
                <div style={{ minWidth: 0 }}>Cliente</div>
                <div>Origine</div>
                <div>Mail Sender</div>
                <div>N. Ordine</div>
                <div>ID</div>
                <div style={{ textAlign: 'right' }}>Righe</div>
              </div>

              {filteredOrders.map((order) => {
                const meta = STATUS_META[order.status] || { label: order.status || '—', color: 'var(--text-3)', bg: 'var(--bg-subtle)', border: 'var(--hairline)' }
                return (
                  <button key={order.job_id} style={s.listRow} onClick={() => selectOrder(order)}>
                    <div>
                      <span style={{
                        ...s.statusBadge,
                        color: meta.color,
                        background: meta.bg,
                        border: `1px solid ${meta.border}`,
                      }}>
                        {meta.label}
                      </span>
                    </div>
                    <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>
                      {formatDateTime(order.timestamp)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={s.orderItemName}>{order.client_name || order.sender || '—'}</div>
                      <div style={s.listRowSub}>{order.subject || order.sender}</div>
                    </div>
                    <div style={s.originRow}>
                      {orderOrigin(order) === 'mail' ? <Mail size={12} /> : <FileText size={12} />}
                      {orderOrigin(order) === 'mail' ? 'Mail' : 'Documento'}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      {order.sender ? (
                        <span style={{ fontSize: 12.5, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {order.sender}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Nessuno</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                      {order.order_number || '—'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {order.job_id || '—'}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--text-2)' }}>
                      {order.line_count ?? '—'}
                    </div>
                  </button>
                )
              })}
              {filteredOrders.length === 0 && (
                <div style={s.listEmpty}>Nessun ordine corrisponde ai filtri.</div>
              )}
            </div>
          )}
        </div>
      )}

      {state === 'review' && currentJob && (
        <div style={s.reviewLayout} className="animate-fade-in">
          <div style={s.reviewTopBar}>
            <button style={s.backToListBtn} onClick={backToList}>← Ordini</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {currentJob.elapsed_seconds != null && (
                <span style={s.elapsedText}>
                  elaborato in {currentJob.elapsed_seconds.toFixed(2).replace('.', ',')}s
                </span>
              )}
            </div>
          </div>

          <div
            style={{
              ...s.reviewBody,
              flexDirection: isMobile ? 'column' : 'row',
              cursor: isDragging ? 'col-resize' : 'default',
              userSelect: isDragging ? 'none' : 'auto',
            }}
            ref={reviewBodyRef}
          >
            {/* Documento originale */}
            <div style={isMobile ? { ...s.pdfPanel, flex: '0 0 42vh' } : { ...s.pdfPanel, flex: `0 0 ${leftWidthPct}%` }}>
              {currentJob.email_body && currentJob.email_body.trim() && (
                <details style={s.emailBodyBox}>
                  <summary style={s.emailBodySummary}>Testo email originale</summary>
                  <div style={s.emailBodyText}>{currentJob.email_body}</div>
                </details>
              )}
              {pdfDocuments.length > 0 ? (
                <div style={s.multiPdfScroller} ref={multiPdfScrollerRef}>
                  {pdfDocuments.map((doc) => (
                    <div key={doc.filename} style={s.multiPdfBlock}>
                      <div style={s.multiPdfLabel}>{doc.filename}</div>
                      <PdfViewer
                        pdfBase64={doc.base64}
                        highlightText={hoveredLineText}
                        scrollRequest={scrollRequest}
                        onError={setPdfError}
                        lines={currentJob.attachment_lines?.[doc.filename]}
                        linesSource={currentJob.attachment_lines_source?.[doc.filename]}
                        confirmedTexts={confirmedLineTexts}
                        scrollContainerRef={multiPdfScrollerRef}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <PdfViewer
                    pdfBase64={null}
                    textContent={!attachmentLoading ? currentJob.body : null}
                    originalFile={originalFile}
                    onError={setPdfError}
                  />
                </div>
              )}
            </div>

            {/* Divisore ridimensionabile — solo su desktop, su mobile i pannelli sono impilati */}
            {!isMobile && (
              <div style={s.resizeHandle} onMouseDown={() => setIsDragging(true)}>
                <div style={{ ...s.resizeBar, background: isDragging ? 'var(--jenia-violet)' : 'var(--hairline-strong)' }} />
                {/* Indizio visivo che il divisore si trascina — piccolissimo
                    e quasi invisibile su richiesta, non deve distrarre dal
                    contenuto, solo far intuire che lì si può ridimensionare. */}
                <ChevronsLeftRight size={15} style={s.resizeHint} />
              </div>
            )}

            {/* Campi estratti */}
            <div style={{ ...s.rightPanel, flex: 1, maxWidth: 'none' }}>
              <OrderReview
                job={currentJob}
                jeniaOrderNumber={jeniaOrderNumbers.get(currentJob?.job_id)}
                onLineHover={(line) => setHoveredLineText(line?.raw_text || line?.description || null)}
                onLineActivate={(line) => setScrollRequest((prev) => ({ text: line?.raw_text || line?.description || null, nonce: prev.nonce + 1 }))}
                onConfirmedLinesChange={setConfirmedLineTexts}
                systemErrors={systemErrors}
                onOverridesChange={(next) => { overridesRef.current = next }}
              />

              {currentJob.status === 'approved' ? (
                <div style={s.actionButtons}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                    Ordine approvato — non modificabile.
                  </span>
                </div>
              ) : currentJob.status === 'rejected' ? (
                <div style={s.actionButtons}>
                  <button
                    className="btn btn-primary"
                    onClick={handleReopen}
                    disabled={actionLoading}
                    style={{ flex: 1 }}
                  >
                    <RotateCcw size={16} />
                    {actionLoading ? 'Riapertura...' : 'Modifica'}
                  </button>
                </div>
              ) : (
              <div style={s.actionButtons}>
                <button
                  className="btn btn-primary"
                  onClick={() => setPendingAction('approve')}
                  disabled={actionLoading}
                  style={{ flex: 1 }}
                >
                  <Check size={16} />
                  {actionLoading ? 'Concludendo...' : 'Concludi ordine'}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => setPendingAction('reject')}
                  disabled={actionLoading}
                  style={{ flex: 1 }}
                >
                  <X size={16} />
                  {actionLoading ? 'Rifiuto...' : 'Rifiuta'}
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(state === 'approved' || state === 'rejected') && (
        <div style={s.idleContainer} className="animate-slide-up">
          <div style={s.resultCard}>
            <div style={s.resultIconWrap}>
              {state === 'approved' ? (
                <Check size={20} strokeWidth={1.8} color="#2F7D54" />
              ) : (
                <X size={20} strokeWidth={1.8} color="#A8494C" />
              )}
            </div>
            <h2 style={s.resultTitle}>
              {state === 'approved' ? 'Fatto.' : 'Ordine annullato.'}
            </h2>
            <p style={s.resultSubtitle}>
              {state === 'approved'
                ? 'Ho registrato l\'ordine e salvato i dati.'
                : 'L\'ordine è stato rifiutato.'}
            </p>
            <button className="btn btn-primary btn-lg" onClick={handleReset} style={{ marginTop: 22 }}>
              <RotateCcw size={14} strokeWidth={1.7} />
              Nuovo ordine
            </button>
          </div>
        </div>
      )}

      {showNewOrderModal && (
        <NewOrderModal
          onClose={() => setShowNewOrderModal(false)}
          onCreate={handleCreateManualOrder}
        />
      )}

      <ConfirmActionModal
        open={pendingAction === 'approve'}
        title="Concludere l'ordine?"
        message="Una volta concluso, l'ordine non sarà più modificabile: la scrittura a database è definitiva."
        confirmLabel="Concludi ordine"
        onConfirm={() => { setPendingAction(null); handleApprove() }}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmActionModal
        open={pendingAction === 'reject'}
        title="Rifiutare l'ordine?"
        message="Potrai modificarlo in seguito: un ordine rifiutato resta nella revisione e può essere ripreso in qualsiasi momento."
        confirmLabel="Rifiuta"
        tone="danger"
        onConfirm={() => { setPendingAction(null); handleReject() }}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  )
}

const s = {
  layout: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  listContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
    overflow: 'hidden',
  },
  listHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  listTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  listSubtitle: {
    fontSize: 13,
    color: 'var(--text-2)',
    margin: '4px 0 0 0',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  refreshBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    height: 34,
    border: '1px solid var(--hairline-strong)',
    borderRadius: 10,
    color: 'var(--text-2)',
    background: '#FFFFFF',
    fontWeight: 500,
    fontSize: 13,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  newOrderBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    height: 34,
    border: 'none',
    borderRadius: 10,
    color: '#FFFFFF',
    fontWeight: 500,
    fontSize: 13,
    fontFamily: 'inherit',
    background: 'linear-gradient(135deg, #38bdf8 0%, #3b82f6 50%, #1e40af 100%)',
    boxShadow: '0 4px 14px rgba(59,130,246,0.20), 0 2px 6px rgba(30,64,175,0.10)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  listTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: 'var(--text)',
    margin: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 220,
    padding: '8px 12px',
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 13,
    fontFamily: 'inherit',
    background: 'transparent',
    color: 'var(--text)',
  },
  filterTabs: {
    display: 'flex',
    gap: 4,
    padding: 4,
    background: 'var(--bg-subtle)',
    border: '1px solid var(--hairline)',
    borderRadius: 999,
  },
  filterTab: {
    padding: '6px 14px',
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'var(--text-2)',
    background: 'transparent',
    border: 'none',
    borderRadius: 999,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 120ms ease, color 120ms ease',
  },
  filterTabActive: {
    background: '#FFFFFF',
    color: 'var(--jenia-violet)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  filterBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    height: 34,
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'var(--text-2)',
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  filterBtnActive: {
    borderColor: 'var(--jenia-violet)',
    color: 'var(--jenia-violet)',
    background: 'var(--grad-jenia-soft)',
  },
  dateFilterPopover: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 12,
    width: 220,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(10,10,10,0.10)',
  },
  dateFilterRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  dateFilterLabel: {
    fontSize: 11.5,
    fontWeight: 500,
    color: 'var(--text-2)',
  },
  dateFilterInput: {
    padding: '6px 8px',
    fontSize: 12.5,
    border: '1px solid var(--hairline)',
    borderRadius: 8,
    fontFamily: 'inherit',
    color: 'var(--text)',
    background: 'var(--bg)',
  },
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    fontSize: 10,
    fontWeight: 600,
    color: '#FFFFFF',
    background: 'var(--jenia-violet)',
    flexShrink: 0,
  },
  listTable: {
    flex: 1,
    overflow: 'auto',
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
  },
  mobileList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  mobileCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 3,
    width: '100%',
    padding: '14px 16px',
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  mobileCardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  mobileCardFooter: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 12px',
    marginTop: 8,
    paddingTop: 8,
    borderTop: '1px solid var(--hairline)',
    fontSize: 11.5,
    color: 'var(--text-3)',
  },
  listGridColumns: '100px 110px 1.4fr 100px 1.1fr 0.9fr 70px 60px 70px',
  listRowHeader: {
    display: 'grid',
    gridTemplateColumns: '100px 110px 1.4fr 100px 1.1fr 0.9fr 70px 60px 70px',
    gap: 16,
    padding: '10px 16px',
    borderBottom: '1px solid var(--hairline)',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-3)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  listRow: {
    display: 'grid',
    gridTemplateColumns: '100px 110px 1.4fr 100px 1.1fr 0.9fr 70px 60px 70px',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    padding: '14px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid var(--hairline)',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: 13,
    fontFamily: 'inherit',
    transition: 'background 120ms ease',
  },
  originRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--text-2)',
    whiteSpace: 'nowrap',
  },
  listRowSub: {
    fontSize: 11.5,
    color: 'var(--text-3)',
    marginTop: 2,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 500,
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  statusBadgeOk: {
    color: '#2F7D54',
    background: 'rgba(47,125,84,0.08)',
    border: '1px solid rgba(47,125,84,0.22)',
  },
  statusBadgeDanger: {
    color: '#A8494C',
    background: 'rgba(168,73,76,0.06)',
    border: '1px solid rgba(168,73,76,0.22)',
  },
  backToListBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
  },
  idleContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  listEmpty: {
    padding: '32px 16px',
    textAlign: 'center',
    color: 'var(--text-3)',
    fontSize: 13,
  },
  reviewLayout: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  reviewTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    padding: '12px 20px',
    borderBottom: '1px solid var(--hairline)',
    background: '#FFFFFF',
  },
  elapsedText: {
    fontSize: 12.5,
    color: 'var(--text-3)',
  },
  simBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-2)',
    background: 'var(--bg-subtle)',
    border: '1px solid var(--hairline-strong)',
    padding: '5px 12px',
    borderRadius: 999,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  reviewBody: {
    flex: 1,
    display: 'flex',
    gap: 12,
    padding: 12,
    overflow: 'hidden',
  },
  pdfPanel: {
    flex: 1,
    minWidth: 0,
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  emailBodyBox: {
    flex: '0 0 auto',
    borderBottom: '1px solid var(--hairline)',
    background: 'var(--bg-subtle)',
  },
  emailBodySummary: {
    cursor: 'pointer',
    padding: '8px 12px',
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--text-2)',
  },
  emailBodyText: {
    padding: '0 12px 12px',
    fontSize: 12.5,
    color: 'var(--text-2)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 160,
    overflowY: 'auto',
  },
  // Colonna scrollabile con tutti gli allegati PDF impilati, uno sotto
  // l'altro, ciascuno con la propria label — sostituisce le freccette
  // avanti/indietro (tolte su richiesta 2026-08-13: con allegati multipli si
  // vedeva sempre e solo il primo, e l'evidenziatore non poteva funzionare
  // su un documento non visibile). `position:'relative'` e' necessario: e'
  // lui l'offsetParent rispetto a cui PdfViewer calcola dove scrollare per
  // portare in vista un riquadro evidenziato (vedi PdfViewer.jsx
  // `scrollContainerRef`).
  multiPdfScroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    position: 'relative',
    background: '#FFFFFF',
  },
  // Contenitore di un singolo allegato nella colonna — deliberatamente SENZA
  // `position`: non deve diventare un offsetParent intermedio, altrimenti
  // l'offsetTop delle pagine si fermerebbe qui invece di arrivare fino a
  // multiPdfScroller (vedi commento sopra).
  multiPdfBlock: {
    borderBottom: '8px solid var(--bg-subtle)',
  },
  multiPdfLabel: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-2)',
    background: 'var(--bg-subtle)',
    borderBottom: '1px solid var(--hairline)',
  },
  resizeHandle: {
    position: 'relative',
    flex: '0 0 14px',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    cursor: 'col-resize',
  },
  // Icona centrata sopra la barra (resizeBar sotto stretch sull'intera
  // altezza, questa invece è un puntino fisso a metà) — colore quasi
  // fuso con lo sfondo apposta, deve suggerire "qui si trascina" senza
  // saltare all'occhio.
  resizeHint: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // Prima --hairline-strong (praticamente invisibile) + 10px: su
    // richiesta, un po' più grande e con un grigio più leggibile (stesso
    // colore delle label ARTICOLO/UM/ecc., vedi lineFieldLabel in
    // OrderReview.jsx) — resta comunque discreto, non salta all'occhio.
    color: 'var(--text-3)',
    pointerEvents: 'none',
  },
  resizeBar: {
    width: 2,
    borderRadius: 999,
    transition: 'background 120ms ease',
  },
  sidebar: {
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    padding: 12,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid var(--hairline)',
  },
  sidebarTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
    margin: 0,
  },
  sidebarCount: {
    fontSize: 12,
    color: 'var(--text-3)',
    background: 'var(--bg-subtle)',
    padding: '2px 8px',
    borderRadius: 4,
  },
  ordersList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  orderItem: {
    padding: '8px 10px',
    background: 'var(--bg-subtle)',
    border: '1px solid transparent',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 120ms ease',
    textAlign: 'left',
    fontSize: 12,
  },
  orderItemActive: {
    background: 'var(--jenia-violet)',
    color: '#FFFFFF',
    border: '1px solid var(--jenia-violet)',
  },
  orderItemName: {
    fontWeight: 600,
    marginBottom: 3,
  },
  orderItemAmount: {
    fontSize: 11,
    opacity: 0.8,
  },
  centerPanel: {
    background: '#FFFFFF',
    border: '1px solid var(--hairline)',
    borderRadius: 12,
    padding: 16,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  recapHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottom: '1px solid var(--hairline)',
  },
  recapTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text)',
    margin: 0,
  },
  recapAmount: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--jenia-violet)',
  },
  recapBody: {
    flex: 1,
    overflow: 'auto',
  },
  rightPanel: {
    flex: '1 1 480px',
    maxWidth: 560,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  actionButtons: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
  },
  resultCard: {
    background: '#FFFFFF',
    border: '1px solid rgba(59,130,246,0.12)',
    borderRadius: 24,
    padding: '40px 44px',
    maxWidth: 480,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    boxShadow: '0 12px 48px rgba(59,130,246,0.10), 0 4px 16px rgba(30,64,175,0.05)',
  },
  resultIconWrap: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: 'var(--bg-subtle)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  resultTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: 8,
  },
  resultSubtitle: {
    fontSize: 13,
    color: 'var(--text-3)',
    lineHeight: 1.6,
    marginBottom: 0,
  },
}
